import { GameWeek } from '@shared/constants';
import { GameID, IGame, IGameStats, IMatchup, ITeamDetails, TeamID } from '@shared/types';
import { useAppSelector } from 'app/hooks';
import { getGameResults, getScheduleByYear } from 'app/slices/GameScheduleSlice';
import { getSelectedYear } from 'app/slices/GamesSlice';
import { getTeams } from 'app/slices/LeagueSlice';
import { breakUID } from 'helpers/IdHelpers';
import { getHomeAndAwayTeamsByGameID } from 'helpers/TeamHelpers';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IGameInfo, Maybe, RootState } from 'types';

const useResultCalculator = () => {
  const gameResults: IGameStats[] = useAppSelector(getGameResults);
  const teams: ITeamDetails[] = useAppSelector(getTeams);
  const yearID = useAppSelector(getSelectedYear);
  const scheduleByYear = useSelector((state: RootState) => getScheduleByYear(state, yearID));

  const [gameResultsLoaded, setGameResultsLoaded] = useState(false);

  useEffect(() => {
    if (teams.length > 0 && gameResults.length > 0) {
      setGameResultsLoaded(true);
    }
  }, [gameResults, teams]);

  const getGameStats = useCallback(
    (weekID: GameWeek, teamID: TeamID) => {
      let gameStats: Maybe<IGameStats> = undefined;
      if (gameResultsLoaded && teams.length > 0) {
        const team = teams.find((team) => team.teamID === teamID);
        const gameUID = team ? team.scheduleByYear[yearID].find((game) => game.indexOf(weekID) > -1) : null;

        if (!gameUID) return gameStats;

        const gameID: GameID = breakUID(gameUID).gameID;

        gameStats = gameResults.find((game) => game.gameID === gameID);
      }
      return gameStats;
    },
    [gameResultsLoaded, teams, gameResults]
  );

  const getGameResult = useCallback(
    (weekID: GameWeek, teamID: TeamID) => {
      if (scheduleByYear && teams.length > 0) {
        const team = teams.find((team) => team.teamID === teamID);
        const gameUID = team ? team.scheduleByYear[yearID].find((game) => game.indexOf(weekID) > -1) : null;

        if (!gameUID) return '';

        const gameID: GameID = breakUID(gameUID).gameID;

        const game: Maybe<IGame | IMatchup> = scheduleByYear.find((game) => game.gameID === gameID);
        if (game) {
          if (game.gameStatus === 'Scheduled' && game.gameStatusCode === '0') return 'DNP';

          const isHomeTeam = game.teamIDHome === teamID;
          const result = isHomeTeam ? game.homeResult : game.awayResult;
          return result || 'DNP';
        }
      }

      return '';
    },
    [gameResultsLoaded, teams, gameResults, scheduleByYear]
  );

  const getGameInfo = useCallback(
    (weekID: GameWeek, teamID: TeamID): Maybe<IGameInfo> => {
      const team = teams.find((t) => t.teamID === teamID);
      if (team) {
        const gameUID = team.scheduleByYear[yearID].find((game) => game.indexOf(weekID) > -1);
        if (gameUID && !gameUID.endsWith('BYE')) {
          const gameID = breakUID(gameUID).gameID;
          const { homeTeamID, awayTeamID, homeAbrv, awayAbrv } = getHomeAndAwayTeamsByGameID(gameID);
          return { homeTeamID, awayTeamID, homeAbrv, awayAbrv, gameID };
        }
      }
      return undefined;
    },
    [teams, yearID]
  );

  return { getGameResult, getGameStats, getGameInfo };
};

export default useResultCalculator;
